import { getInnerVersion } from './getInnerVersion';
import { getPlatform } from '../env/platform/getPlatform';

/**
 * 内部版本号比较参数
 */
interface InnerVersionCompareOptions {
  /** Android 平台目标版本号 */
  androidVersion?: number | string;
  /** iOS 平台目标版本号 */
  iosVersion?: number | string;
  /** 纯血鸿蒙目标版本号 */
  harmonyOsVersion?: number | string;
}

/**
 * 判断当前平台的内部版本号是否大于等于输入值
 *
 * 获取当前应用的内部版本号，根据平台选择对应的目标版本号进行比较。
 * 版本号格式通常为 "FUI037.08.389"（iOS）、"FUG037.08.389"（Android）或 "FUHM037.08.389"（纯血鸿蒙），
 * 函数会自动提取数字部分进行比较。
 *
 * @param options - 版本比较选项
 * @returns 当前平台内部版本是否大于等于目标版本
 *
 * @example
 * ```ts
 * import { isHigherInnerVersion } from '@fu/matrix';
 *
 * // 当前版本为 "FUI037.08.389"，iOS 平台
 * isHigherInnerVersion({ iosVersion: '037.08.100', androidVersion: '037.08.000' }); // => true
 *
 * // 当前版本为 "FUG037.08.389"，Android 平台
 * isHigherInnerVersion({ iosVersion: '037.08.000', androidVersion: '037.08.100' }); // => true
 *
 * // 版本号不足时
 * isHigherInnerVersion({ iosVersion: '037.09.000', androidVersion: '037.09.000' }); // => false
 *
 * // 没有内部版本号时
 * isHigherInnerVersion({ iosVersion: '037.08.000' }); // => false
 * ```
 *
 * @since 1.3.0
 */
export function isHigherInnerVersion(options: InnerVersionCompareOptions): boolean {
  const { androidVersion, iosVersion, harmonyOsVersion } = options;

  // 获取内部版本号
  const innerVersion = getInnerVersion();
  if (innerVersion) {
    const _platform = getPlatform();
    const currentVersionNumber = getNumberFromVersion(innerVersion);

    let _finalVersion = 0;
    // iOS
    if ('iphone' === _platform) {
      _finalVersion = getNumberFromVersion(iosVersion);
      // 纯血鸿蒙
    } else if ('harmony' === _platform) {
      _finalVersion = getNumberFromVersion(harmonyOsVersion);
      // Android（包含套皮鸿蒙/其他）
    } else {
      _finalVersion = getNumberFromVersion(androidVersion);
    }

    return parseInt(String(_finalVersion), 10) <= parseInt(String(currentVersionNumber), 10);
  }
  return false;
}

/**
 * 从版本号字符串中提取数字
 *
 * @param version - 版本号，可以是数字或字符串
 * @returns 提取的数字版本号
 *
 * @example
 * ```ts
 * getNumberFromVersion('FUG037.08.455'); // => 3708455
 * getNumberFromVersion('FUI037.08.389'); // => 3708389
 * getNumberFromVersion('FUHM037.08.389'); // => 3708389
 * getNumberFromVersion(3708455); // => 3708455
 * getNumberFromVersion('3708455'); // => 3708455
 * ```
 */
function getNumberFromVersion(version: string | number | undefined): number {
  // 如果是数字类型直接返回
  if ('number' === typeof version) {
    return version;
  }
  return Number((version || '999.99.999').split('.').slice(0, 3).join('').replace(/\D/g, ''));
}